import type {
  CmmdForgeCloudEnvironmentRow,
  CmmdForgeCloudProjectRow,
  ForgeCloudCapacity,
} from "./cloudProjectCatalogContracts";

export type {
  CmmdForgeCloudEnvironmentRow,
  CmmdForgeCloudProjectRemovalRow,
  CmmdForgeCloudProjectRow,
  CmmdRenamedForgeCloudEnvironment,
  ForgeCloudCapacity,
} from "./cloudProjectCatalogContracts";

/**
 * The Forge Cloud project catalog, and the ONE place that is allowed to decide
 * whether a project is absent from it.
 *
 * WHY THIS FILE EXISTS
 *
 * A completed durable-removal receipt proves the saga reached its terminal
 * phase, but the user-visible outcome still requires a fresh catalog read.
 * Removal therefore proves itself by RE-READING this catalog and asserting the
 * row is gone.
 *
 * That makes "the catalog does not list it" a proof of removal — and every
 * correctness bug on this route has been the same mistake in a new place:
 * something that could be empty/absent for a reason OTHER than absence was read
 * as absence anyway.
 *
 *   1. a 200 with no `projects` key defaulted `rows` to `[]`;
 *   2. the retired packaged-desktop direct leg passed `response.projects` through, so an absent
 *      key gave `rows: undefined` that every caller's `?? []` folded back into
 *      "empty";
 *   3. a catalog row with `runtime: null` could not be matched to a connected
 *      sidebar row, and "no match" was read as "not there";
 *   4. a catalog row bound to a DIFFERENT runtime id is exactly as unmatchable
 *      as a null one, and was read as "not there" too.
 *
 * Each was closed on its own and the next one surfaced in the next review, so
 * this module closes the SHAPE instead:
 *
 *   - `readCloudProjectCatalog` is the only constructor of a catalog value.
 *     Every read — proxy leg, packaged-desktop bearer leg, SSE frame — goes
 *     through it, and a body it cannot understand THROWS rather than yielding an
 *     empty catalog.
 *   - `CloudProjectCatalog` is a class with a private field. There is no object
 *     literal that inhabits the type, so `catalog ?? { rows: [] }` and friends
 *     do not compile — the `?? []` door is shut at the type level.
 *   - the absence question is answered only by `locate()`, which returns a value
 *     whose ONLY member is `match()`. There is no tag to test, no array to
 *     measure and no boolean to negate, so a caller cannot reach "absent"
 *     without also writing the `unprovable` branch. Omitting it is a compile
 *     error; taking it silently is a deliberate, reviewable act.
 *
 * Instances 5 and 6 then arrived from the OTHER direction, and closing them is
 * what `CloudRemovalTarget` below is for. Both were the same move: an id from a
 * source that could be stale (a caller's option; the localStorage-seeded runtime
 * -list snapshot) was allowed to become the id a removal saga targeted, because
 * `locate()` had vouched for the ROW. Vouching for the row is not vouching for
 * the claim — instance 6 deleted a stranger's registration and left the clicked
 * project on screen. So the durable removal path no longer takes an unvouched number:
 *
 *   - `CloudRemovalTarget` is a class whose value binding is NOT exported. Only
 *     the type escapes this module, its field is `#private`, and the sole place
 *     that constructs one is `locate()`'s match on a row THIS catalog holds.
 *     There is no literal, no cast that survives, and no second factory.
 *   - The sidebar resolves a `CloudRemovalTarget` before it can start the saga.
 *     A cached number cannot be spelled into that parameter, so the `?? ` that
 *     caused instance 6 is not a policy anyone must remember — it does not compile.
 */

/**
 * How the SIDEBAR ROW THE USER CLICKED names itself to the catalog.
 *
 * Both fields must be things the ROW inherently IS, never things some other
 * source believes about it. There is deliberately no channel here for a cached
 * or caller-supplied id: such a value can only ever answer "does the catalog
 * contain a row with this number?", which is a different question from "which
 * row did the user click?", and answering the first as though it were the second
 * is instances 5 and 6.
 *
 *  - `registrationProjectId` — this row IS that registration. Only a pre-connect
 *    catalog row can say this, because such a row exists only as a rendering of
 *    a catalog registration and carries its id as its own identity.
 *    `projectId` is present on EVERY catalog row, so this makes every row
 *    decidable: any row with a different `projectId` is positively ruled out.
 *    It is the only field that can prove ABSENCE.
 *
 *  - `runtimeId` — the runtime a connected sidebar row is talking to. It may
 *    MATCH a row's runtime overlay, but it may NEVER rule one out. A catalog
 *    row's overlay is not pinned to the runtime the sidebar connected through:
 *    CMMD picks a non-superseded runtime row with no ORDER BY (#66) and
 *    reprovision paths mint new ids, so `row.runtime` being null OR naming a
 *    different runtime are the SAME situation — this reader cannot see whether
 *    that row is the project or not. Instance 3 and instance 4 are that one
 *    sentence, discovered twice.
 *
 *  - `cmmdEnvironmentId` — the durable Environment a connected row is addressed
 *    by (FOR-32652). Same overlay and same rule as `runtimeId`: it may MATCH a
 *    row, never rule one out. Without it a durable row, whose legacy locator is
 *    null, can name nothing to this catalog at all.
 */
export interface ClickedCloudRowIdentity {
  readonly registrationProjectId?: number;
  readonly runtimeId?: string;
  readonly cmmdEnvironmentId?: string;
}

/**
 * A registration this catalog has just NAMED for the row that was looked up —
 * and the only value a destructive cloud-project call will accept.
 *
 * The class binding is not exported: outside this module the name exists only
 * as a type. `#registrationProjectId` is a private field, so no object literal
 * and no `as` cast produces an inhabitant either. The single construction site
 * in the entire program is `locate()`'s `matched` branch below, which can only
 * pass a row this catalog is holding.
 *
 * Consequence, and the point of the exercise: a cached id, a caller-supplied id
 * or any other number CANNOT be spelled where a `CloudRemovalTarget` is
 * required. Reintroducing instance 6 — `claimedId ?? provenId` at the call to
 * the durable removal call — is not a policy violation a reviewer has to catch, it is
 * `Type 'number' is not assignable to type 'CloudRemovalTarget'`.
 */
class CatalogNamedRemovalTarget {
  readonly #registrationProjectId: number;

  private constructor(registrationProjectId: number) {
    this.#registrationProjectId = registrationProjectId;
  }

  /**
   * @internal Reachable only from `locate()`; the class binding never leaves
   * this module, so there is no second caller anywhere.
   */
  static namedByCatalog(row: CmmdForgeCloudProjectRow): CatalogNamedRemovalTarget {
    return new CatalogNamedRemovalTarget(row.projectId);
  }

  /**
   * The id, for building the durable saga request. READ-only and one-directional: this
   * getter takes a proof and yields a number, and nothing anywhere converts a
   * number back into a proof.
   */
  get registrationProjectId(): number {
    return this.#registrationProjectId;
  }

  /**
   * The identity the CONFIRMING re-read asks with.
   *
   * A registration id decides every catalog row, so the re-read can answer
   * "provably absent" — and it is entitled to, because this id is the one the
   * live catalog named for the clicked row rather than one this code guessed.
   * The runtime id is deliberately NOT carried over: it can only add matches,
   * and a catalog whose overlay has since moved to a neighbouring row (#66)
   * would then report a completed removal as "still listed".
   */
  asConfirmationIdentity(): ClickedCloudRowIdentity {
    return { registrationProjectId: this.#registrationProjectId };
  }
}

export type CloudRemovalTarget = CatalogNamedRemovalTarget;

export interface CloudCatalogLookupHandlers<T> {
  /**
   * The catalog lists this project, and has NAMED the row it matched. The
   * target is the only thing handed back — deliberately not a bare number, so
   * "present" cannot be used as an excuse to delete some other id.
   */
  readonly present: (found: { readonly target: CloudRemovalTarget }) => T;
  /** Every row was positively ruled out (or there are no rows). The project is NOT registered. */
  readonly provablyAbsent: () => T;
  /**
   * Neither could be established. `reason` is diagnostic only — the caller must
   * treat this as "unknown", which on a destructive path means refuse.
   */
  readonly unprovable: (reason: string) => T;
}

/**
 * The result of asking the catalog about a project.
 *
 * Deliberately opaque: `match` is the only member, so the three outcomes cannot
 * be collapsed into a boolean, a tag comparison or an array length. Writing
 * "unknown counts as gone" requires typing it out in the `unprovable` handler,
 * where a reviewer can see it.
 */
export interface CloudCatalogLookup {
  readonly match: <T>(handlers: CloudCatalogLookupHandlers<T>) => T;
}

function lookupPresent(target: CloudRemovalTarget): CloudCatalogLookup {
  return { match: (handlers) => handlers.present({ target }) };
}

function lookupProvablyAbsent(): CloudCatalogLookup {
  return { match: (handlers) => handlers.provablyAbsent() };
}

function lookupUnprovable(reason: string): CloudCatalogLookup {
  return { match: (handlers) => handlers.unprovable(reason) };
}

/**
 * A catalog body that `readCloudProjectCatalog` has already validated.
 *
 * Constructed nowhere else — the constructor is private and the class carries a
 * private field, so no object literal is assignable to it. That is what stops
 * the next `?? []`: there is no empty catalog to fall back to that the compiler
 * will accept.
 */
export class CloudProjectCatalog {
  readonly #rows: readonly CmmdForgeCloudProjectRow[];
  readonly #environments: readonly CmmdForgeCloudEnvironmentRow[];

  /**
   * `null` whenever CMMD has not resolved the warm-pool capacity. A DEFAULT is
   * correct here and nowhere else in this file: capacity only greys out a hint,
   * an older CMMD deploy legitimately omits it, and nothing proves a project's
   * absence from it.
   */
  readonly capacity: ForgeCloudCapacity | null;

  private constructor(
    rows: readonly CmmdForgeCloudProjectRow[],
    environments: readonly CmmdForgeCloudEnvironmentRow[],
    capacity: ForgeCloudCapacity | null,
  ) {
    this.#rows = rows;
    this.#environments = environments;
    this.capacity = capacity;
  }

  /** @internal Only `readCloudProjectCatalog` may build one. */
  static fromValidatedBody(
    rows: readonly CmmdForgeCloudProjectRow[],
    environments: readonly CmmdForgeCloudEnvironmentRow[],
    capacity: ForgeCloudCapacity | null,
  ): CloudProjectCatalog {
    return new CloudProjectCatalog(rows, environments, capacity);
  }

  /**
   * The rows, for RENDERING only.
   *
   * Never answer "is this project gone?" from this array — that is the bug class
   * this module exists to close, and `locate()` is the answer. The name is long
   * on purpose: `catalog.listRowsForDisplay().length === 0` should read as
   * wrong at a glance.
   */
  listRowsForDisplay(): readonly CmmdForgeCloudProjectRow[] {
    return this.#rows;
  }

  listEnvironmentsForDisplay(): readonly CmmdForgeCloudEnvironmentRow[] {
    return this.#environments;
  }

  /**
   * Ask the catalog which row — if any — IS the sidebar row that was clicked.
   *
   * PRESENT when a row matches an identity the ROW itself carries, and the
   * answer is that ROW, wrapped as a `CloudRemovalTarget`. This is the single
   * place in the program where a destructive cloud-project call gets its id, so
   * "which row did the user click?" and "what may be deleted?" are answered by
   * the same expression and cannot drift apart.
   *
   * PROVABLY ABSENT only when the catalog can be exhausted:
   *  - a registration id the row inherently IS decides every row, because
   *    `projectId` is present on all of them; or
   *  - there are no rows at all, in which case nothing is registered.
   *
   * UNPROVABLE otherwise: the row offered only an overlay locator and some row
   * went unmatched, so this reader cannot see whether that row is the project.
   * "I could not tell" must never be handed back as "it is not there".
   */
  locate(identity: ClickedCloudRowIdentity): CloudCatalogLookup {
    // A registration id DECIDES, on its own, and the runtime id is not consulted
    // when one is present. `projectId` is on every row, so this single lookup is
    // exhaustive in both directions: it names the row or it proves there is
    // none. Combining the two identities — "try the id, else try the runtime" —
    // would reintroduce the shape this module exists to prevent: two candidate
    // answers and an arbitrary rule for choosing between them. There is no `??`
    // here on purpose.
    if (identity.registrationProjectId !== undefined) {
      const named = this.#rows.find((row) => row.projectId === identity.registrationProjectId);
      return named === undefined
        ? lookupProvablyAbsent()
        : lookupPresent(CatalogNamedRemovalTarget.namedByCatalog(named));
    }

    if (identity.runtimeId !== undefined || identity.cmmdEnvironmentId !== undefined) {
      // Each locator is compared only when the clicked row offered it, because
      // `environmentId` is optional on the overlay and undefined-to-undefined
      // would name a stranger. Neither rules a row out, so a miss falls through.
      const named = this.#rows.find(
        (row) =>
          row.runtime != null &&
          ((identity.runtimeId !== undefined && row.runtime.runtimeId === identity.runtimeId) ||
            (identity.cmmdEnvironmentId !== undefined &&
              row.runtime.environmentId === identity.cmmdEnvironmentId)),
      );
      if (named) return lookupPresent(CatalogNamedRemovalTarget.namedByCatalog(named));
    }

    if (this.#rows.length === 0) return lookupProvablyAbsent();

    const locator = identity.runtimeId ?? identity.cmmdEnvironmentId;
    return lookupUnprovable(
      locator === undefined
        ? "no identity this catalog can be matched on"
        : `the catalog holds ${String(this.#rows.length)} row(s) that ${locator} cannot be ruled out against`,
    );
  }
}

/**
 * THE validator. Every catalog read on every surface goes through this function.
 *
 * Tolerates both the bare `{projects, capacity}` the Forge proxy forwards and
 * the `{data:{projects, capacity}}` envelope CMMD returns on the packaged
 * desktop's direct-bearer leg.
 *
 * `capacity` may be missing — that is genuinely unknown-and-harmless, so it
 * defaults to `null`. `projects` gets the OPPOSITE treatment and the asymmetry
 * is the point: removal proves a project gone by finding it absent from these
 * rows, so a body this client cannot understand must never be allowed to read as
 * an empty catalog. Defaulting it turned "I could not parse this" into "the
 * project is proven gone" — the unknown-treated-as-proven defect, sitting inside
 * the check that exists to kill it. Throw instead.
 */
export function readCloudProjectCatalog(raw: unknown): CloudProjectCatalog {
  const body = raw as Record<string, unknown> | null;
  const unwrapped =
    body && typeof body === "object" && "data" in body && body["data"]
      ? (body["data"] as Record<string, unknown>)
      : body;
  const projects = unwrapped?.["projects"];
  if (!Array.isArray(projects)) {
    throw new Error(
      "Forge Cloud returned a project catalog this version cannot read (no `projects` list).",
    );
  }
  const environments = unwrapped?.["environments"];
  if (!Array.isArray(environments)) {
    throw new Error(
      "Forge Cloud returned an Environment catalog this version cannot read (invalid `environments` list).",
    );
  }
  if (!projects.every(isCloudProjectCatalogRecord)) {
    throw new Error(
      "Forge Cloud returned a project catalog this version cannot read (invalid project entry).",
    );
  }
  if (!environments.every(isCloudEnvironmentCatalogRecord)) {
    throw new Error(
      "Forge Cloud returned an Environment catalog this version cannot read (invalid Environment entry).",
    );
  }
  return CloudProjectCatalog.fromValidatedBody(
    projects,
    environments,
    (unwrapped?.["capacity"] as ForgeCloudCapacity | null | undefined) ?? null,
  );
}

function isCloudProjectCatalogRecord(value: unknown): value is CmmdForgeCloudProjectRow {
  if (
    !isRecord(value) ||
    typeof value.organizationId !== "number" ||
    typeof value.projectId !== "number"
  ) {
    return false;
  }
  return (
    value.runtime === undefined || value.runtime === null || isCloudRuntimeRecord(value.runtime)
  );
}

function isCloudEnvironmentCatalogRecord(value: unknown): value is CmmdForgeCloudEnvironmentRow {
  return (
    isRecord(value) &&
    typeof value.organizationId === "number" &&
    typeof value.userId === "number" &&
    isCloudRuntimeRecord(value.runtime)
  );
}

function isCloudRuntimeRecord(
  value: unknown,
): value is NonNullable<CmmdForgeCloudProjectRow["runtime"]> {
  return isRecord(value) && typeof value.runtimeId === "string" && typeof value.status === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
